import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/design-system/lib/cn';

/**
 * Botão da aplicação. Todo botão tem rótulo textual — `size="icon"` é a única exceção e
 * por isso exige `aria-label` (ver o tipo `ButtonProps`).
 *
 * `ai` é o gradiente reservado à geração inteligente; `dashed` é a ação de adicionar
 * dentro de uma lista (vaga, função, ministério).
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-control font-semibold whitespace-nowrap',
    'transition-[background-color,border-color,color,filter] disabled:opacity-50',
    '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-[1.8]',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white hover:bg-primary-hover disabled:hover:bg-primary',
        ai: 'bg-ai text-white hover:brightness-110',
        secondary: 'border border-line-input bg-white font-medium text-ink hover:bg-surface-subtle',
        soft: 'border border-primary-line bg-white text-primary-hover hover:bg-primary-soft',
        ghost: 'bg-transparent font-medium text-ink-secondary hover:bg-surface-muted',
        link: 'h-auto bg-transparent px-0 font-semibold text-primary hover:text-primary-hover',
        destructive:
          'border border-status-conflict-line bg-white font-medium text-status-conflict hover:bg-danger-soft',
        danger: 'bg-danger text-white hover:bg-status-conflict',
        dark: 'bg-ink text-white hover:bg-ink-strong',
        dashed:
          'border-[1.5px] border-dashed border-primary-hover-line bg-white text-primary hover:bg-primary-faint',
      },
      size: {
        lg: 'h-10.5 px-5 text-body',
        md: 'h-10 px-4 text-control',
        sm: 'h-9 px-3 text-small',
        xs: 'h-8 rounded-control-sm px-3 text-meta',
        icon: 'size-9.5 p-0',
        'icon-sm': 'size-9 rounded-control-sm p-0',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0' }],
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

type ButtonBaseProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Renderiza no elemento filho (`<a>`, `<Link>`) mantendo o estilo do botão. */
    asChild?: boolean;
  };

/**
 * Botão só de ícone não tem rótulo visível, então o nome acessível é obrigatório — o
 * tipo recusa a chamada sem `aria-label`.
 */
export type ButtonProps =
  | (ButtonBaseProps & { size: 'icon' | 'icon-sm'; 'aria-label': string })
  | (ButtonBaseProps & { size?: Exclude<ButtonBaseProps['size'], 'icon' | 'icon-sm'> });

export function Button({ className, variant, size, asChild = false, type, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
